import { AvatarProps } from './Avatar';
import { Theme, css } from '@emotion/react';

const sizes: Record<AvatarProps['size'], number> = {
  xs: 20,
  sm: 24,
  md: 32,
  lg: 44,
};

const getBorderWidth = (props: AvatarProps) => {
  switch (props.size) {
    case 'xs':
      return 1;
    case 'sm':
      return 2;
    case 'md':
      return 3;
    case 'lg':
      return 4;
  }
};

const getStackOverlap = (props: AvatarProps) => {
  switch (props.size) {
    case 'xs':
      return -5;
    case 'sm':
      return -6;
    case 'md':
      return -7;
    case 'lg':
      return -8;
  }
};

export const root = (props: AvatarProps) => [
  css`
    position: relative;
    user-select: none;
    display: inline-block;
    box-sizing: border-box;
  `,
  props.inStack &&
    css`
      border-radius: 50%;
      border-style: solid;
      border-color: ${props.parentBackgroundColor};
      border-width: ${getBorderWidth(props)}px;

      &:not(:last-of-type) {
        margin-left: ${getStackOverlap(props)}px;
      }
    `,
];

const getColorStyles = (props: AvatarProps) => {
  switch (props.variant) {
    case 'border':
      return css`
        border: solid 2px ${props.color};

        ${props.inStack &&
        css`
          background-color: ${props.parentBackgroundColor};
        `};
      `;
    case 'filled':
      return css`
        background-color: ${props.color};
      `;
    default:
      return null;
  }
};

const getLayoutStyles = (props: AvatarProps) => {
  switch (props.variant) {
    case 'border':
    case 'filled':
      return css`
        display: flex;
        align-items: center;
        justify-content: center;
      `;
    case 'image':
      return css`
        overflow: hidden;

        > img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      `;
    default:
      return null;
  }
};

export const container = (props: AvatarProps) => [
  css`
    width: ${sizes[props.size]}px;
    height: ${sizes[props.size]}px;
    border-radius: 50%;
  `,
  getColorStyles(props),
  getLayoutStyles(props),
  props.isDimmed &&
    css`
      opacity: 0.25;
    `,
];

const getBadgeOffset = (props: AvatarProps) => {
  switch (props.size) {
    case 'xs':
      return css`
        top: -4px;
        right: -4px;
      `;
    case 'sm':
      return css`
        top: -3px;
        right: -3px;
      `;
    case 'md':
      return css`
        top: -2px;
        right: -2px;
      `;
    case 'lg':
      return css`
        top: 1px;
        right: 1px;
      `;
  }
};

export const badge = (props: AvatarProps) => (theme: Theme) => [
  css`
    position: absolute;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    z-index: 1;
    border: solid 2px ${theme.colors.borderContrast};
    background-color: ${theme.colors.bgPrimaryBase};
  `,
  getBadgeOffset(props),
];

const getIconOffset = (props: AvatarProps) => {
  switch (props.size) {
    case 'xs':
    case 'sm':
      return null;
    case 'md':
      return css`
        bottom: -6px;
        right: -8px;
      `;
    case 'lg':
      return css`
        bottom: 0;
        right: -10px;
      `;
  }
};

export const icon = (props: AvatarProps) => [
  ({ colors }: Theme) => css`
    position: absolute;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    z-index: 1;
    border: solid 2px ${colors.borderContrast};
    background-color: ${colors.bgPrimaryBase};

    display: flex;
    justify-content: center;
    align-items: center;

    svg {
      color: ${colors.fgContrast};
      width: 16px;
      height: 16px;
    }
  `,
  getIconOffset(props),
];

export const button = css`
  &:hover:not(:disabled) {
    filter: brightness(75%);
  }

  &:disabled {
    opacity: 75%;
  }
`;

export const text = css`
  letter-spacing: 0;
`;
